import { useEffect, useCallback } from 'react';

export interface SolutionModalData {
  id: string;
  icon: string;
  title: string;
  description: string;
  category: string;
  longDescription: string;
  features: string[];
}

interface SolutionModalProps {
  solution: SolutionModalData | null;
  onClose: () => void;
}

export default function SolutionModal({ solution, onClose }: SolutionModalProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!solution) return;
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [solution, handleKeyDown]);

  if (!solution) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal card */}
      <div className="relative w-full max-w-[600px] max-h-[85vh] bg-background-50 rounded-2xl overflow-hidden shadow-2xl animate-scale-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-background-50/90 backdrop-blur-sm border border-background-200/60 flex items-center justify-center hover:bg-background-100 transition-colors duration-200 cursor-pointer"
          aria-label="Close modal"
        >
          <i className="ri-close-line text-foreground-700 w-4 h-4 flex items-center justify-center" />
        </button>

        {/* Gradient banner */}
        <div className="relative h-44 overflow-hidden">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,oklch(var(--primary-700)/0.9),oklch(var(--background-800)/0.95))]">
            <div
              className="absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 20px, oklch(var(--background-50)/0.5) 20px, oklch(var(--background-50)/0.5) 21px)`,
              }}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-transparent" />

          {/* Icon */}
          <div className="absolute bottom-6 left-6 flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-background-50/95 border border-background-200/40 flex items-center justify-center shadow-sm">
              <i className={`${solution.icon} text-2xl text-primary-600 w-7 h-7 flex items-center justify-center`} />
            </div>
            <div>
              <span className="inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-background-50/90 border border-background-200/40 text-foreground-700 mb-1">
                {solution.category}
              </span>
              <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                {solution.title}
              </h3>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(85vh-11rem)]">
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
            {solution.longDescription}
          </p>

          <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">
            What&apos;s Included
          </h4>

          <ul className="space-y-3 mb-8">
            {solution.features.map((feature) => (
              <li key={feature} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-accent-100 dark:bg-accent-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-accent-600 dark:text-accent-400 text-[10px] w-3.5 h-3.5 flex items-center justify-center" />
                </div>
                <span
                  className="text-sm text-foreground-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: feature }}
                />
              </li>
            ))}
          </ul>

          {/* CTA */}
          <a
            href="#cta"
            onClick={(e) => {
              e.preventDefault();
              onClose();
              setTimeout(() => {
                document.querySelector('#cta')?.scrollIntoView({ behavior: 'smooth' });
              }, 200);
            }}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-950 text-background-50 dark:text-foreground-950 dark:bg-foreground-50 text-sm font-semibold hover:bg-foreground-800 dark:hover:bg-foreground-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Get a Free Consultation
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
          </a>
        </div>
      </div>
    </div>
  );
}